<template>
  <div
    class="recording flex items-center gap-x-5 rounded-4 border"
    :class="{
      'border-blue-500': isPlaying,
      'rounded-b-none border-b-0': showCallText,
    }"
  >
    <div class="flex w-full items-center divide-x">
      <div
        v-tooltip="
          isPlaying
            ? $t('ACTIVITY.PAUSE_RECORDING')
            : $t('ACTIVITY.PLAY_RECORDING')
        "
        class="button-action w-10 p-2"
        data-cy="play-button"
        @click="handlePlayButtonClick"
      >
        <icon-base
          v-bind="icon"
          :data-cy="isPlaying ? 'pause-icon' : 'play-icon'"
          class="cursor-pointer"
          :class="{ 'text-blue-500': isPlaying }"
        />
      </div>
      <div class="flex grow items-center space-x-3 px-3 py-2">
        <div
          class="flex w-25 items-center space-x-1 text-xs font-semibold text-label"
        >
          <span ref="timePlayedEl" class="min-w-[37px]">00:00</span>
          <span>/</span>
          <span ref="timeTotalEl" class="min-w-[37px]">00:00</span>
        </div>
        <loader :is-loading="isLoading" />
        <div :id="recordingId" class="call-recording grow" />
        <lf-dropdown
          v-tooltip="$t('ACTIVITY.PLAYBACK_SPEED')"
          :options="playbackSpeedOptions"
          name="playback-speed"
          data-cy="playback-speed"
          class="speed-select !w-20 !grow-0"
          :value="playbackSpeedValue"
          :show-warning-icon="false"
          full-width
          as-badge
          hide-placeholder-when-value
          :teleport-options="teleportOptions"
          custom-z-index="z-101"
          @change="handlePlaybackSpeedChange"
        />
      </div>
      <div class="w-12 px-3">
        <div
          v-tooltip="$t('COMMON.DOWNLOAD')"
          class="button-action"
          data-cy="download-button"
          @click.prevent="
            downloadFile(recording.file_url, {}, recording.original_name)
          "
        >
          <icon-base :icon="IconDownload" />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import WaveSurfer from "wavesurfer.js";

import { downloadFile } from "@/helpers/files";
import { useNotification } from "@/hooks/notifications";

import IconDownload from "@/components/icons/IconDownload.vue";
import IconPause from "@/components/icons/IconPause.vue";
import IconPlay from "@/components/icons/IconPlay.vue";
import IconBase from "@/components/ui/IconBase.vue";
import Loader from "@/components/ui/Loader.vue";
import LfDropdown, {
  type DropdownTeleportOptions,
} from "@/components/ui/inputs/LfDropdown.vue";

import type { IFile } from "@/models/common";

const { id, recording, teleportOptions } = defineProps<{
  id: string | number;
  recording: IFile;
  teleportOptions?: Partial<DropdownTeleportOptions>;
}>();

const playbackSpeedOptions = [
  "0.5x",
  "0.75x",
  "1x",
  "1.25x",
  "1.5x",
  "1.75x",
  "2x",
] as const;

const isPlaying = ref(false);
const isLoading = ref(false);
const timePlayedEl = ref<HTMLElement | null>(null);
const timeTotalEl = ref<HTMLElement | null>(null);
const waveSurfer = ref<WaveSurfer | null>(null);
const playbackSpeedValue = ref<(typeof playbackSpeedOptions)[number]>("1x");
const showCallText = ref(false);

const { showMessage } = useNotification();
const { t } = useI18n();

const recordingId = computed(() => {
  return `recording-${id}`;
});

const icon = computed(() => {
  return {
    icon: isPlaying.value ? IconPause : IconPlay,
    width: 14,
    height: 14,
  };
});

const getTimeCodeFromNum = (num: number) => {
  const minutes = Math.floor(num / 60) % 60;
  const seconds = Math.floor(num % 60);
  const secondsWithZero = seconds < 10 ? `0${seconds}` : seconds;
  const minutesWithZero = minutes < 10 ? `0${minutes}` : minutes;
  return `${minutesWithZero}:${secondsWithZero}`;
};

const handlePlayButtonClick = () => {
  isPlaying.value = !isPlaying.value;
  waveSurfer.value?.playPause();
};

const handlePlaybackSpeedChange = (index: string | number) => {
  playbackSpeedValue.value = playbackSpeedOptions[Number(index)];
  const playbackRate = parseFloat(playbackSpeedValue.value);
  waveSurfer.value?.setPlaybackRate(playbackRate);
  isPlaying.value = true;
  waveSurfer.value?.play();
};

onMounted(() => {
  try {
    waveSurfer.value = WaveSurfer.create({
      container: `div #${recordingId.value}`,
      waveColor: "#C2C6CD",
      progressColor: "rgb(55,105,214, 0.7)",
      url: recording.file_url,
      barWidth: 4,
      barGap: 2,
      barRadius: 2,
      height: 30,
      mediaControls: false,
      fetchParams: {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      },
    });
  } catch (error) {
    showMessage(t("ERRORS.CALL_RECORDING_ERROR"), "error");
    return;
  }

  waveSurfer.value?.on("interaction", (newTime: number) => {
    if (!timePlayedEl.value) {
      return;
    }
    timePlayedEl.value.innerText = getTimeCodeFromNum(newTime);
  });
  waveSurfer.value?.on("audioprocess", (newTime: number) => {
    if (!timePlayedEl.value) {
      return;
    }
    timePlayedEl.value.innerText = getTimeCodeFromNum(newTime);
  });

  waveSurfer.value?.on("loading", () => {
    isLoading.value = true;
  });

  waveSurfer.value?.on("ready", () => {
    isLoading.value = false;

    if (!timeTotalEl.value) {
      return;
    }
    timeTotalEl.value.innerText = getTimeCodeFromNum(
      waveSurfer.value?.getDuration() || 0
    );
  });

  waveSurfer.value?.on("finish", () => {
    isPlaying.value = false;
  });
});
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.button-action {
  @apply flex cursor-pointer items-center justify-center self-stretch rounded p-1 hover:bg-gray-100;
}

:deep() {
  .dropdown-option,
  .badge-value {
    @apply text-xs;
  }
}

.speed-select {
  :deep() {
    #exp_elem_list {
      @apply max-h-36;
    }
  }
}
</style>
